"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PoolProvider = exports.usePool = void 0;
var constate_1 = __importDefault(require("constate"));
var dayjs_1 = __importDefault(require("dayjs"));
var react_1 = require("react");
var LaunchpadProvider_1 = require("../../../LaunchpadProvider");
var launchpadConstants_1 = require("../../../launchpadConstants");
var hooks_1 = require("../../../shared/hooks");
var utils_1 = require("../../../shared/utils");
var _contracts_1 = require("../../../contracts");
var NFTSaleLoader_1 = require("../../../pool/hooks/usePool/loaders/NFTSaleLoader");
var ReferralSaleLoader_1 = require("../../../pool/hooks/usePool/loaders/ReferralSaleLoader");
var SaleLoader_1 = require("../../../pool/hooks/usePool/loaders/SaleLoader");
var populateWithDefault_1 = require("../../../pool/hooks/usePool/populateWithDefault");
var types_1 = require("../../../pool/types");
var utils_2 = require("../../../pool/utils");
var initUserState = {
    contributed: launchpadConstants_1.ZERO_BN,
    balance: launchpadConstants_1.ZERO_BN,
    isWhitelisted: false,
    isRegistered: false,
    isLottery: false,
    isLotteryWinner: false,
    totalAlloc: launchpadConstants_1.ZERO_BN,
    wlAlloc: launchpadConstants_1.ZERO_BN,
    fcfsAlloc: launchpadConstants_1.ZERO_BN,
    levelAlloc: launchpadConstants_1.ZERO_BN,
    tierId: '',
    weight: 0,
};
function useRawPool(_a) {
    var _b;
    var config = _a.config, onChange = _a.onChange;
    var account = (0, hooks_1.useUser)().account;
    var _c = (0, LaunchpadProvider_1.useLaunchpadProvider)(), salePools = _c.salePools, defaultSale = _c.defaultSale;
    var _d = (0, hooks_1.useWeb3Provider)(), targetChainWeb3 = _d.targetChainWeb3, staticWeb3 = _d.staticWeb3, chainId = _d.connectedChain.id;
    var randomTime = (0, react_1.useMemo)(function () { return Math.random() * 1000; }, []);
    // When sale is cached, no need to fetch any of its data
    var saleIsCached = ((_b = salePools.find(function (p) { return p.id === config.id; })) === null || _b === void 0 ? void 0 : _b.sale.status) ===
        types_1.SaleStatus.finished;
    var _e = (0, _contracts_1.usePoolContracts)(config, targetChainWeb3), saleInstance = _e.saleInstance, tokenInstance = _e.tokenInstance, fundTokenInstance = _e.fundTokenInstance;
    var _f = (0, _contracts_1.usePoolContracts)(config, staticWeb3), staticSaleInstance = _f.saleInstance, staticFundTokenInstance = _f.fundTokenInstance;
    var _g = (0, react_1.useState)(function () { return (__assign({}, (0, populateWithDefault_1.populateWithDefault)(config.sale, defaultSale))); }), sale = _g[0], setSale = _g[1];
    var _h = (0, react_1.useState)(initUserState), userState = _h[0], setUserState = _h[1];
    var _j = (0, react_1.useState)(true), isLoading = _j[0], setIsLoading = _j[1];
    var _k = (0, react_1.useState)(false), fullyLoaded = _k[0], setFullyLoaded = _k[1];
    var isSaleFinished = sale.status === types_1.SaleStatus.finished;
    var isSoldOut = fullyLoaded && (0, utils_2.isSaleSoldOut)(sale);
    var poolDebug = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return utils_1.debug.apply(void 0, __spreadArray([config.id], args, false));
    };
    var loader = (0, react_1.useMemo)(function () {
        if (!saleInstance)
            return;
        return (0, utils_2.isNFTPool)(config)
            ? new NFTSaleLoader_1.NFTSaleLoader(targetChainWeb3, config.sale, saleInstance)
            : (0, utils_2.isReferralSale)(config.sale)
                ? new ReferralSaleLoader_1.ReferralSaleLoader(targetChainWeb3, config.sale, saleInstance)
                : new SaleLoader_1.SaleLoader(targetChainWeb3, config.sale, saleInstance);
    }, [saleInstance]);
    var initSaleFetcher = (0, react_1.useCallback)(function () {
        if (!loader)
            return;
        return loader
            .fetchInit()
            .then(function (data) {
            // If start date is forced as TBA in config, keep it TBA even if contract is specified
            if (config.sale.startDate === 'TBA') {
                data.startDate = '';
            }
            poolDebug("Fetch:init ".concat(pool.id), data);
            setSale(function (old) { return (__assign(__assign({}, old), data)); });
        })
            .catch(function (e) { return poolDebug('Failed to load init data', e); });
    }, [targetChainWeb3, chainId, sale.address, saleInstance]);
    var regularSaleFetcher = (0, react_1.useCallback)(function (isInit) {
        if (isInit === void 0) { isInit = false; }
        if (!isInit && (sale.update === false || isSaleFinished)) {
            return Promise.resolve();
        }
        if (!loader)
            return;
        return loader
            .fetchRegular()
            .then(function (data) {
            poolDebug("Fetch:regular ".concat(pool.id), data);
            setSale(function (old) { return (__assign(__assign({}, old), data)); });
        })
            .catch(function (e) { return poolDebug('Failed to load regular data', e); });
    }, [targetChainWeb3, chainId, sale.address, saleInstance]);
    var userDataFetcher = (0, react_1.useCallback)(function (account) {
        if (!account || !loader)
            return;
        return loader
            .fetchUser(account)
            .then(function (data) {
            poolDebug("Fetch:user ".concat(pool.id), data);
            setUserState(function (old) { return (__assign(__assign({}, old), data)); });
            return data;
        })
            .catch(function (e) { return poolDebug('Failed to load user data', e); });
    }, [targetChainWeb3, chainId, sale.address, saleInstance]);
    // Fetch data: init (rare updates) and regular (frequent updates)
    (0, react_1.useEffect)(function () {
        if (saleIsCached) {
            setIsLoading(false);
            return;
        }
        if (!saleInstance) {
            if (!sale.address) {
                setIsLoading(false);
            }
            return;
        }
        var loadTimer = setTimeout(function () {
            Promise.all([initSaleFetcher(), regularSaleFetcher(true)]).finally(function () {
                setIsLoading(false);
                setFullyLoaded(true);
            });
        }, randomTime);
        var initTimer = setInterval(initSaleFetcher, launchpadConstants_1.fetchIntervalsMs.init + randomTime);
        var regularTimer = setInterval(regularSaleFetcher, launchpadConstants_1.fetchIntervalsMs.regular + randomTime);
        return function () {
            clearTimeout(loadTimer);
            clearInterval(initTimer);
            clearInterval(regularTimer);
        };
    }, [saleInstance, sale.status, isSaleFinished]);
    (0, react_1.useEffect)(function () {
        if (account) {
            userDataFetcher(account);
            if (!saleIsCached) {
                var timer_1 = setInterval(function () { return userDataFetcher(account); }, launchpadConstants_1.fetchIntervalsMs.regular + randomTime);
                return function () { return clearInterval(timer_1); };
            }
        }
        else {
            setUserState(initUserState);
        }
    }, [account, sale.status]);
    // Status update: on any pool data change and once now = start or end
    var newStatus = (0, utils_2.getSaleStatus)(sale);
    (0, react_1.useEffect)(function () {
        var nextDate = (0, utils_2.getNextSaleDate)(sale);
        if (sale.status !== newStatus) {
            setSale(function (old) { return (__assign(__assign({}, old), { status: newStatus })); });
        }
        if (nextDate) {
            var timeoutMs = nextDate.valueOf() - (0, dayjs_1.default)().valueOf();
            var timerId_1 = setTimeout(function () {
                var status = (0, utils_2.getSaleStatus)(sale);
                setSale(function (old) { return (__assign(__assign({}, old), { status: status })); });
            }, timeoutMs + 1000);
            return function () { return clearTimeout(timerId_1); };
        }
    }, [newStatus]);
    (0, react_1.useEffect)(function () {
        if (isSoldOut) {
            setSale(function (old) { return (__assign(__assign({}, old), { status: types_1.SaleStatus.finished })); });
        }
    }, [isSoldOut]);
    function refresh() {
        regularSaleFetcher();
    }
    var dep = JSON.stringify(sale);
    var pool = (0, react_1.useMemo)(function () { return (__assign(__assign({}, config), { sale: sale })); }, [dep]);
    (0, react_1.useEffect)(function () {
        if (fullyLoaded) {
            onChange === null || onChange === void 0 ? void 0 : onChange(pool);
        }
    }, [onChange, pool]);
    return {
        pool: pool,
        userState: userState,
        // @ts-expect-error idk
        saleInstance: saleInstance,
        tokenInstance: tokenInstance,
        fundTokenInstance: fundTokenInstance,
        // @ts-expect-error idk
        staticSaleInstance: staticSaleInstance,
        staticFundTokenInstance: staticFundTokenInstance,
        isLoading: isLoading,
        refresh: refresh,
        refreshUser: function () {
            if (!account)
                return;
            return userDataFetcher(account);
        },
        isSoldOut: isSoldOut,
        isSoftCapReached: sale.isSoftCap && sale.tokensSold.gt(sale.tokensForSale),
        isFinished: isSoldOut || sale.status === types_1.SaleStatus.finished,
    };
}
var _a = (0, constate_1.default)(useRawPool), _PoolProvider = _a[0], _usePool = _a[1];
exports.usePool = _usePool;
exports.PoolProvider = _PoolProvider;
